import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <div className="min-h-screen" data-id="element-169">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden bg-stone-900 pt-24" data-id="element-170">
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20" data-id="element-171">
          <div className="absolute -top-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-rose-500 blur-3xl" data-id="element-172" />
          <div className="absolute bottom-[10%] -left-[10%] w-[400px] h-[400px] rounded-full bg-amber-500 blur-3xl" data-id="element-173" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center py-16" data-id="element-174">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-175">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-176">
              Get in Touch
            </h1>
            <p className="text-xl text-stone-300 max-w-2xl mx-auto" data-id="element-177">
              Ready to start planning your next celebration? We'd love to hear
              from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white" data-id="element-178">
        <div className="container mx-auto px-4" data-id="element-179">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-180">
            {/* Contact Form */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-181">
              <h2 className="text-3xl font-bold text-stone-900 mb-8" data-id="element-182">
                Send Us a Message
              </h2>

              {isSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-id="element-183">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" data-id="element-184" />
                  <h3 className="text-2xl font-bold text-green-800 mb-2" data-id="element-185">
                    Message Sent!
                  </h3>
                  <p className="text-green-700" data-id="element-186">
                    We'll get back to you as soon as possible.
                  </p>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-187">
                  <div data-id="element-188">
                    <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2" data-id="element-189">
                      Your Name
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all" placeholder="Enter your name" data-id="element-190" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-191">
                    <div data-id="element-192">
                      <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2" data-id="element-193">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all" placeholder="your@email.com" data-id="element-194" />
                    </div>
                    <div data-id="element-195">
                      <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2" data-id="element-196">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all" placeholder="+260 XXX XXX XXX" data-id="element-197" />
                    </div>
                  </div>

                  <div data-id="element-198">
                    <label htmlFor="eventType" className="block text-sm font-medium text-stone-700 mb-2" data-id="element-199">
                      Event Type
                    </label>
                    <select id="eventType" name="eventType" value={formData.eventType} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all bg-white" data-id="element-200">
                      <option value="" data-id="element-201">Select an event type</option>
                      <option value="wedding" data-id="element-202">Wedding</option>
                      <option value="birthday" data-id="element-203">Birthday</option>
                      <option value="special" data-id="element-204">Special Event</option>
                      <option value="other" data-id="element-205">Other</option>
                    </select>
                  </div>

                  <div data-id="element-206">
                    <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2" data-id="element-207">
                      Your Message
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-200 outline-none transition-all resize-none" placeholder="Tell us about your event..." data-id="element-208" />
                  </div>

                  <Button type="submit" size="lg" className="w-full gap-2" data-id="element-209">
                    <Send className="w-5 h-5" data-id="element-210" />
                    Send Message
                  </Button>
                </form>}
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="space-y-8" data-id="element-211">
              <h2 className="text-3xl font-bold text-stone-900 mb-8" data-id="element-212">
                Contact Information
              </h2>

              <div className="space-y-6" data-id="element-213">
                <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100" data-id="element-214">
                  <div className="flex items-start gap-4" data-id="element-215">
                    <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-216">
                      <Phone className="w-6 h-6 text-rose-500" data-id="element-217" />
                    </div>
                    <div data-id="element-218">
                      <h3 className="text-lg font-bold text-stone-900 mb-1" data-id="element-219">
                        WhatsApp / Call
                      </h3>
                      <p className="text-stone-600" data-id="element-220">+260 968642042</p>
                      <a href="https://wa.me/260968642042" target="_blank" rel="noopener noreferrer" className="text-rose-500 font-medium text-sm hover:underline mt-2 inline-block" data-id="element-221">
                        Chat on WhatsApp →
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100" data-id="element-222">
                  <div className="flex items-start gap-4" data-id="element-223">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-224">
                      <Mail className="w-6 h-6 text-amber-500" data-id="element-225" />
                    </div>
                    <div data-id="element-226">
                      <h3 className="text-lg font-bold text-stone-900 mb-1" data-id="element-227">
                        Email Us
                      </h3>
                      <p className="text-stone-600 break-words" data-id="element-228">
                        margret.kapyelata687@gmail.com
                      </p>
                      <a href="mailto:margret.kapyelata687@gmail.com" className="text-amber-600 font-medium text-sm hover:underline mt-2 inline-block" data-id="element-229">
                        Send an email →
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-2xl p-6 border border-stone-100" data-id="element-230">
                  <div className="flex items-start gap-4" data-id="element-231">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-232">
                      <MapPin className="w-6 h-6 text-purple-500" data-id="element-233" />
                    </div>
                    <div data-id="element-234">
                      <h3 className="text-lg font-bold text-stone-900 mb-1" data-id="element-235">
                        Location
                      </h3>
                      <p className="text-stone-600" data-id="element-236">Kitwe, Zambia</p>
                      <p className="text-stone-500 text-sm mt-1" data-id="element-237">
                        Serving the Copperbelt region
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="bg-stone-100 rounded-2xl h-64 flex items-center justify-center border border-stone-200" data-id="element-238">
                <div className="text-center" data-id="element-239">
                  <MapPin className="w-12 h-12 text-stone-400 mx-auto mb-3" data-id="element-240" />
                  <p className="text-stone-500 font-medium" data-id="element-241">Kitwe, Zambia</p>
                  <p className="text-stone-400 text-sm" data-id="element-242">Copperbelt Province</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-16 bg-stone-900 text-white" data-id="element-243">
        <div className="container mx-auto px-4 text-center" data-id="element-244">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-245">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-246">
              Prefer to chat directly?
            </h2>
            <p className="text-stone-300 mb-8 max-w-xl mx-auto" data-id="element-247">
              WhatsApp is the fastest way to reach us. We typically respond
              within a few hours.
            </p>
            <a href="https://wa.me/260968642042" target="_blank" rel="noopener noreferrer" data-id="element-248">
              <Button size="lg" className="bg-[#25D366] hover:bg-[#128C7E] text-white border-none shadow-lg gap-2" data-id="element-249">
                <MessageCircle className="w-5 h-5" data-id="element-250" />
                Chat on WhatsApp
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}